import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getMobileStands } from '../services/mobileStandService';
import { deliverOrder, getKitchenOrders } from '../services/invoiceService';
import { createSocket } from '../services/socket';
import { describePortionChanges } from '../utils/portions';
import '../styles/pantalla.css';

const STAND_KEY = 'origenes_pantalla_stand';
const FACTURACION_STAND_KEY = 'origenes_facturacion_stand';
const SOUND_KEY = 'origenes_pantalla_sonido';

// Tiempo que un pedido se marca como "Nuevo"
const NEW_MS = 45 * 1000;
// Duración de la animación de salida (debe coincidir con pantalla.css)
const LEAVE_MS = 380;

function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* sin almacenamiento */ }
}

function formatTime(date) {
  return new Date(date).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
}

// Campanita de dos tonos con Web Audio (sin archivos de sonido)
function playChime(ctx) {
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume();
  const t = ctx.currentTime;
  [[880, 0], [1320, 0.16]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t + delay);
    gain.gain.exponentialRampToValueAtTime(0.35, t + delay + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.6);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t + delay);
    osc.stop(t + delay + 0.65);
  });
}

export default function Pantalla() {
  const [stands, setStands] = useState([]);
  const [standId, setStandId] = useState(
    () => readStorage(STAND_KEY, '') || readStorage(FACTURACION_STAND_KEY, '')
  );
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connection, setConnection] = useState('connecting'); // connecting | online | offline
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState({});      // id -> true mientras anima la salida
  const [delivering, setDelivering] = useState({}); // id -> true mientras espera la API
  const [fresh, setFresh] = useState({});           // id -> timestamp de llegada por socket
  const [checked, setChecked] = useState({});       // `${orderId}-${detailId}` -> true
  const [soundOn, setSoundOn] = useState(() => readStorage(SOUND_KEY, false));
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [toast, setToast] = useState('');

  const audioRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;

  useEffect(() => { writeStorage(STAND_KEY, standId); }, [standId]);
  useEffect(() => { writeStorage(SOUND_KEY, soundOn); }, [soundOn]);

  // Reloj y etiqueta "Nuevo": se actualizan cada segundo
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    getMobileStands()
      .then(data => setStands(Array.isArray(data) ? data.filter(s => s.state) : []))
      .catch(() => setStands([]));
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const loadOrders = useCallback(async () => {
    if (!standId) { setOrders([]); return; }
    setLoading(true);
    setError('');
    try {
      const data = await getKitchenOrders(standId);
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudieron cargar los pedidos.');
    } finally {
      setLoading(false);
    }
  }, [standId]);

  // Retira un pedido con animación
  const removeOrder = useCallback((id) => {
    setLeaving(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setOrders(prev => prev.filter(o => o.id !== id));
      setLeaving(({ [id]: _, ...rest }) => rest);
      setFresh(({ [id]: _, ...rest }) => rest);
    }, LEAVE_MS);
  }, []);

  // Conexión en tiempo real
  useEffect(() => {
    if (!standId) return undefined;

    loadOrders();
    const socket = createSocket();
    let connectedBefore = false;

    socket.on('connect', () => {
      setConnection('online');
      socket.emit('kitchen:join', Number(standId));
      // Tras una reconexión se recarga por si se perdió algún evento
      if (connectedBefore) loadOrders();
      connectedBefore = true;
    });
    socket.on('disconnect', () => setConnection('offline'));
    socket.on('connect_error', () => setConnection('offline'));

    socket.on('kitchen:new', (order) => {
      setOrders(prev => (prev.some(o => o.id === order.id) ? prev : [...prev, order]));
      setFresh(prev => ({ ...prev, [order.id]: Date.now() }));
      if (soundOnRef.current) playChime(audioRef.current);
    });
    socket.on('kitchen:removed', ({ id }) => removeOrder(id));

    return () => {
      socket.disconnect();
      setConnection('connecting');
    };
  }, [standId, loadOrders, removeOrder]);

  async function handleDeliver(id) {
    setDelivering(prev => ({ ...prev, [id]: true }));
    try {
      await deliverOrder(id);
      removeOrder(id);
    } catch (err) {
      const msg = err.response?.data?.msg || 'No se pudo marcar como entregado.';
      setToast(msg);
      // Si ya no está pendiente (entregado en otra tablet o anulado), se retira igual
      if (err.response?.status === 400 || err.response?.status === 404) removeOrder(id);
    } finally {
      setDelivering(({ [id]: _, ...rest }) => rest);
    }
  }

  function toggleSound() {
    // El navegador solo permite audio después de una interacción del usuario
    if (!audioRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioRef.current = new Ctx();
    }
    const next = !soundOn;
    setSoundOn(next);
    if (next) playChime(audioRef.current);
  }

  function toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  }

  function toggleItem(key) {
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const selectedStand = stands.find(s => String(s.id) === String(standId));
  const pending = orders.filter(o => !leaving[o.id]);

  return (
    <div className="kds">
      <header className="kds-header">
        <div className="kds-brand">
          <div className="kds-logo">O</div>
          <div>
            <div className="kds-title">Cocina</div>
            <div className="kds-subtitle">{selectedStand ? selectedStand.name : 'Pedidos en tiempo real'}</div>
          </div>
        </div>

        <div className="kds-stats">
          <div className="kds-stat">
            <span className="kds-stat-value">{pending.length}</span>
            <span className="kds-stat-label">Pendientes</span>
          </div>
        </div>

        <div className="kds-controls">
          <select
            className="kds-select"
            value={standId}
            onChange={(e) => setStandId(e.target.value)}
            aria-label="Stand móvil"
          >
            <option value="">Seleccione un stand…</option>
            {stands.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <span className={`kds-status ${connection}`} title="Estado de la conexión en tiempo real">
            <span className="kds-dot" />
            {connection === 'online' ? 'En vivo' : connection === 'offline' ? 'Sin conexión' : 'Conectando'}
          </span>

          <button
            type="button"
            className={`kds-icon-btn ${soundOn ? 'active' : ''}`}
            onClick={toggleSound}
            title={soundOn ? 'Silenciar avisos' : 'Activar sonido de nuevos pedidos'}
          >
            <i className={`bi ${soundOn ? 'bi-bell-fill' : 'bi-bell-slash'}`} />
          </button>
          <button
            type="button"
            className="kds-icon-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`} />
          </button>

          <div className="kds-clock">
            {new Date(now).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </header>

      {error && (
        <div className="kds-banner">
          <i className="bi bi-exclamation-triangle" /> {error}
          <button type="button" onClick={loadOrders}>Reintentar</button>
        </div>
      )}

      <main className="kds-board">
        {!standId ? (
          <EmptyState icon="bi-shop-window" title="Seleccione un stand" text="Elija el stand móvil cuyos pedidos desea ver en esta pantalla." />
        ) : loading && !orders.length ? (
          <EmptyState icon="bi-hourglass-split" title="Cargando pedidos…" />
        ) : !orders.length ? (
          <EmptyState icon="bi-check2-circle" title="Todo al día" text="Los nuevos pedidos aparecerán aquí automáticamente al facturarse." />
        ) : (
          <div className="kds-grid">
            {orders.map((order, index) => {
              const isNew = fresh[order.id] && now - fresh[order.id] < NEW_MS;
              const units = order.details.reduce((sum, d) => sum + d.quantity, 0);

              return (
                <article
                  key={order.id}
                  className={`kds-ticket ${isNew ? 'is-new' : ''} ${leaving[order.id] ? 'is-leaving' : ''}`}
                >
                  <div className="kds-ticket-head">
                    <div>
                      <div className="kds-order-no">
                        <span className="kds-seq">{index + 1}</span>
                        Pedido #{String(order.id).padStart(4, '0')}
                        {isNew && <span className="kds-new-badge">Nuevo</span>}
                      </div>
                      <div className="kds-meta">
                        <i className="bi bi-person" /> {order.client?.name || 'Cliente'}
                      </div>
                    </div>
                    <div className="kds-time">
                      <i className="bi bi-clock" />
                      {formatTime(order.date)}
                    </div>
                  </div>

                  <ul className="kds-items">
                    {order.details.map(detail => {
                      const key = `${order.id}-${detail.id}`;
                      const changes = describePortionChanges(
                        (detail.portionItems || []).map(p => ({
                          name: p.portion?.name || '',
                          quantity: p.quantity,
                          recipeQuantity: p.recipeQuantity,
                        }))
                      );
                      return (
                        <li
                          key={detail.id}
                          className={`kds-item ${checked[key] ? 'done' : ''}`}
                          onClick={() => toggleItem(key)}
                        >
                          <span className="kds-qty">{detail.quantity}</span>
                          <div className="kds-item-body">
                            <div className="kds-item-name">{detail.menuProduct?.name || 'Producto'}</div>
                            {changes.length > 0 && (
                              <div className="kds-mods">
                                {changes.map((c, i) => (
                                  <span key={i} className={`kds-mod ${c.type}`}>{c.text}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <i className={`bi ${checked[key] ? 'bi-check-circle-fill' : 'bi-circle'} kds-check`} />
                        </li>
                      );
                    })}
                  </ul>

                  {order.observations && (
                    <div className="kds-note">
                      <i className="bi bi-chat-left-text" />
                      <span>{order.observations}</span>
                    </div>
                  )}

                  <div className="kds-ticket-foot">
                    <span className="kds-units">{units} {units === 1 ? 'unidad' : 'unidades'}</span>
                    <button
                      type="button"
                      className="kds-deliver"
                      disabled={!!delivering[order.id] || !!leaving[order.id]}
                      onClick={() => handleDeliver(order.id)}
                    >
                      {delivering[order.id]
                        ? <span className="kds-spinner" />
                        : <i className="bi bi-check2-all" />}
                      Entregado
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {toast && <div className="kds-toast"><i className="bi bi-info-circle" /> {toast}</div>}
    </div>
  );
}

function EmptyState({ icon, title, text }) {
  return (
    <div className="kds-empty">
      <div className="kds-empty-icon"><i className={`bi ${icon}`} /></div>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}
